import { useTranslation } from 'react-i18next';
import {
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/atoms/dialog';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Badge } from '@/components/atoms/badge';
import { Checkbox } from '@/components/atoms/checkbox';
import { Skeleton } from '@/components/atoms/skeleton';
import { Search, Package, ChevronLeft, Loader2 } from 'lucide-react';
import { StepIndicator } from './StepIndicator';
import { MAX_PRODUCTS } from './constants';
import { getImageUrl } from '@/lib/utils';
import type { VariationType, AssociationGroup, Product } from '@/lib/types';

interface SelectProductsStepProps {
  selectedGroup: AssociationGroup | null;
  searchQuery: string;
  selectedProductIds: string[];
  availableProducts: Product[];
  isLoadingProducts: boolean;
  isAddingProducts: boolean;
  variationType: VariationType;
  onSetSearchQuery: (query: string) => void;
  onToggleProduct: (productId: string) => void;
  onBack: () => void;
  onContinue: () => void;
}

export function SelectProductsStep({
  selectedGroup,
  searchQuery,
  selectedProductIds,
  availableProducts,
  isLoadingProducts,
  isAddingProducts,
  variationType,
  onSetSearchQuery,
  onToggleProduct,
  onBack,
  onContinue,
}: SelectProductsStepProps) {
  const { t } = useTranslation();

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t('associations.selectProducts')}</DialogTitle>
        <DialogDescription>
          {selectedGroup && (
            <span className="flex items-center gap-2">
              {t('associations.addingTo')}: <Badge>{selectedGroup.name}</Badge>
            </span>
          )}
        </DialogDescription>
      </DialogHeader>

      <StepIndicator step="select_products" variationType={variationType} />

      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => onSetSearchQuery(e.target.value)}
            placeholder={t('products.searchPlaceholder')}
            className="pl-9"
          />
        </div>

        <div className="text-sm text-muted-foreground">
          {t('associations.selectedCount', { count: selectedProductIds.length, max: MAX_PRODUCTS })}
        </div>

        <div className="space-y-2 max-h-[300px] overflow-y-auto">
          {isLoadingProducts ? (
            <div className="space-y-2">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : availableProducts.length > 0 ? (
            availableProducts.map((product) => {
              const productId = product.refId || product.id;
              const isSelected = selectedProductIds.includes(productId);
              const isDisabled = !isSelected && selectedProductIds.length >= MAX_PRODUCTS;
              const imageUrl = Array.isArray(product.images) ? getImageUrl(product.images[0]) : null;

              return (
                <label
                  key={productId}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    isSelected ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                  } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => !isDisabled && onToggleProduct(productId)}
                    disabled={isDisabled}
                  />
                  <div className="size-12 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                    {imageUrl ? (
                      <img src={imageUrl} alt={product.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="flex h-full items-center justify-center text-muted-foreground">
                        <Package className="size-6" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{product.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {t('products.stock')}: {product.stock}
                    </p>
                  </div>
                </label>
              );
            })
          ) : (
            <p className="text-center text-muted-foreground py-4">
              {t('products.noProducts')}
            </p>
          )}
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onBack}>
          <ChevronLeft className="size-4 mr-1" />
          {t('common.back')}
        </Button>
        <Button
          onClick={onContinue}
          disabled={selectedProductIds.length === 0 || isAddingProducts}
        >
          {isAddingProducts && <Loader2 className="size-4 mr-2 animate-spin" />}
          {selectedGroup?.variationType === 'related' ? t('common.confirm') : t('common.next')}
        </Button>
      </DialogFooter>
    </>
  );
}
